'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { RefObject } from 'react';
import React, { useEffect, useImperativeHandle, useRef, useState, useTransition } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import getCaretCoordinates from 'textarea-caret';
import type { UndoRedoSnapshot } from './hooks/use-undo-redo';
import type { AutosizeTextAreaProps, AutosizeTextAreaRef } from './types';
import { TextareaButtons } from './components/textarea-buttons';
import { useAutosizeTextArea } from './hooks/use-autosize-textarea';
import { useMarkdownShortcuts } from './hooks/use-markdown-shortcuts';
import { useUndoRedo } from './hooks/use-undo-redo';
import { TEXTAREA_VARIANTS } from './types';
import { handleFileUpload, insertGif } from './utils/file-handling';
import { createMarkdownLinkFromPastedUrl } from './utils/markdown-formatters';
import { shouldShowMentions } from './utils/mention-utils';
import { MentionableUsersDropdown } from '@/components/textarea/components/mentionable-users-dropdown';
import { useCollaborators } from '@/data/collaborators/use-collaborators';
import { mapCollaboratorsToUsers } from '@/data/collaborators/utils';
import { useRepository } from '@/data/repository/use-repository.client';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { validateFile } from '@/lib/files/file-uploads';
import { cn } from '@/lib/utils';
export const AutosizeTextarea = ({
  ref,
  owner,
  repo,
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 52,
  className,
  onChange,
  showSendButton = true,
  variant = TEXTAREA_VARIANTS.default,
  onAiSuggestion,
  replyCodeContext,
  fileContext,
  defaultValue,
  value,
  enableUserMentions,
  handleSubmit,
  handleCancel,
  isStreamingText,
  onAddToReview,
  disablePostImmediately,
  extraButtonNodes,
  onGetCodeLine,
  side,
  ...props
}: AutosizeTextAreaProps & {
  ref?: React.RefObject<AutosizeTextAreaRef | null>;
}) => {
  const [_isPending, startTransition] = useTransition();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const initialValue = value as string ?? defaultValue ?? '';
  const [triggerAutoSize, setTriggerAutoSize] = useState<string>(initialValue);
  const initialValueRef = useRef(initialValue);
  const currentValueRef = useRef(initialValue);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoadingAiSuggestion, setIsLoadingAiSuggestion] = useState(false);
  const isGeneratingAiSuggestionRef = useRef(false);
  const [showMentionsDropdown, setShowMentionsDropdown] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState('');
  const [mentionStartPosition, setMentionStartPosition] = useState<number | null>(null);
  const [mentionCoordinates, setMentionCoordinates] = useState<{
    left: number;
    top: number;
    height: number;
  } | null>(null);
  const isHandlingPopoverEscape = useRef(false);
  const {
    data: collaborators
  } = useCollaborators(owner, repo);
  const mentionableUsers = mapCollaboratorsToUsers(collaborators);
  const {
    data: repository,
    isLoading: isLoadingRepository
  } = useRepository(owner, repo);

  // Add the cubic user to the list of mentionable users
  mentionableUsers.push({
    // Ensure the bot appears in the mention typeahead under its correct handle
    login: 'cubic-dev-ai',
    avatar_url: 'https://avatars.githubusercontent.com/in/1082092?v=4',
    html_url: 'https://github.com/cubic-dev-ai'
  });
  const hasText = Boolean(triggerAutoSize?.trim().length > 0);
  const undoRedo = useUndoRedo();
  const commitTextareaValue = (nextValue: string, options: {
    batch?: boolean;
    selectionStart?: number;
    emitChange?: boolean;
    trackHistory?: boolean;
    changeEvent?: React.ChangeEvent<HTMLTextAreaElement>;
  } = {}) => {
    const selectionStart = options.selectionStart ?? textAreaRef.current?.selectionStart ?? nextValue.length;
    currentValueRef.current = nextValue;
    setTriggerAutoSize(nextValue);
    if (options.emitChange ?? true) {
      onChange?.(options.changeEvent ?? {
        target: {
          value: nextValue
        }
      } as React.ChangeEvent<HTMLTextAreaElement>);
    }
    if (options.trackHistory ?? true) {
      undoRedo.pushState({
        value: nextValue,
        selectionStart
      }, {
        batch: options.batch
      });
    }
  };
  const applySnapshot = (snapshot: UndoRedoSnapshot) => {
    commitTextareaValue(snapshot.value, {
      emitChange: true,
      selectionStart: snapshot.selectionStart,
      trackHistory: false
    });
    requestAnimationFrame(() => {
      const ta = textAreaRef.current;
      if (ta && document.body.contains(ta)) {
        ta.focus();
        ta.setSelectionRange(snapshot.selectionStart, snapshot.selectionStart);
      }
    });
  };
  useMarkdownShortcuts({
    textAreaRef: textAreaRef as RefObject<HTMLTextAreaElement>,
    onChange,
    setTriggerAutoSize: nextValue_0 => {
      currentValueRef.current = nextValue_0;
      setTriggerAutoSize(nextValue_0);
    },
    onValueChange: (nextValue_1, selectionStart_0) => undoRedo.pushState({
      value: nextValue_1,
      selectionStart: selectionStart_0
    })
  });
  useEffect(() => {
    undoRedo.pushState({
      value: initialValueRef.current,
      selectionStart: initialValueRef.current.length
    });
  }, [undoRedo]);

  // Undo: mod+z
  useHotkeys('mod+z', e => {
    if (textAreaRef.current !== document.activeElement) {
      return;
    }
    e.preventDefault();
    const snapshot_0 = undoRedo.undo();
    if (snapshot_0) {
      applySnapshot(snapshot_0);
    }
  }, {
    enableOnFormTags: true
  });

  // Redo: mod+shift+z on macOS, ctrl+y on Windows
  useHotkeys(['mod+shift+z', 'ctrl+y'], e_0 => {
    if (textAreaRef.current !== document.activeElement) {
      return;
    }
    e_0.preventDefault();
    const snapshot_1 = undoRedo.redo();
    if (snapshot_1) {
      applySnapshot(snapshot_1);
    }
  }, {
    enableOnFormTags: true
  });

  // Track if we've already focused the textarea for editing saved content
  const hasInitiallyFocused = useRef(false);

  // When the textarea is focused, set the cursor to the end of the text if we're editing saved content
  useEffect(() => {
    if (textAreaRef.current && variant === TEXTAREA_VARIANTS.editingSavedContent && !hasInitiallyFocused.current) {
      const el = textAreaRef.current;
      const length = triggerAutoSize.length;

      // Find the nearest scrollable ancestor so we can restore its scroll
      // position after focus + setSelectionRange.  Pages may scroll inside a
      // custom container (e.g. #scrollable-content) rather than the window.
      let scrollParent: HTMLElement | null = el.parentElement;
      while (scrollParent) {
        const {
          overflowY
        } = getComputedStyle(scrollParent);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          break;
        }
        scrollParent = scrollParent.parentElement;
      }
      const savedScrollTop = scrollParent?.scrollTop ?? 0;
      el.focus({
        preventScroll: true
      });
      el.setSelectionRange(length, length);

      // Restore — setSelectionRange on a not-yet-autosized textarea causes
      // the browser to scroll the container to keep the cursor visible.
      if (scrollParent) {
        scrollParent.scrollTo({
          top: savedScrollTop
        });
      }
      hasInitiallyFocused.current = true;
    }
  }, [variant, triggerAutoSize.length]);

  // Add effect to handle external value updates
  useEffect(() => {
    if (value === undefined || value === currentValueRef.current) {
      return;
    }
    const selectionStart_1 = textAreaRef.current?.selectionStart ?? (value as string).length;
    currentValueRef.current = value as string;
    setTriggerAutoSize(value as string);
    undoRedo.reset({
      value: value as string,
      selectionStart: selectionStart_1
    });
  }, [undoRedo, value]);
  const handleTextChange = (e_1: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e_1.target.value;
    const cursorPosition = e_1.target.selectionStart;

    // Whether we should show the mentions dropdown
    if (shouldShowMentions(newValue, cursorPosition, enableUserMentions ?? false)) {
      const textarea = textAreaRef.current;
      if (textarea) {
        const coords = getCaretCoordinates(textarea, cursorPosition);

        // Use startTransition for non-urgent UI updates
        startTransition(() => {
          setMentionCoordinates(coords);
          setShowMentionsDropdown(true);
          setMentionStartPosition(cursorPosition - 1);
          setMentionSearchQuery('@');
        });
      }
    } else if (showMentionsDropdown && mentionStartPosition !== null) {
      // Update mention search query instead of the input if we're in mention mode
      const searchText = newValue.slice(mentionStartPosition, cursorPosition);

      // Only update if we're still in a mention context (starts with @ and no spaces)
      if (searchText.startsWith('@') && !searchText.includes(' ')) {
        startTransition(() => {
          setMentionSearchQuery(searchText);
        });
      } else {
        console.log('Closing mentions dropdown - invalid search text');
        startTransition(() => {
          setMentionCoordinates(null);
          setShowMentionsDropdown(false);
          setMentionStartPosition(null);
        });
      }
    }

    // Handle general text change after mentions
    commitTextareaValue(newValue, {
      batch: true,
      changeEvent: e_1,
      selectionStart: cursorPosition
    });
  };

  /**
   * Handles the selection of a user or team from the mentions dropdown.
   * Inserts the selected mention at the current cursor position and updates the textarea value.
   */
  const handleMentionSelect = (user: {
    id: string;
    name: string;
  }) => {
    const selectedMentionStartPosition = mentionStartPosition;
    setShowMentionsDropdown(false);
    setMentionStartPosition(null);
    setMentionCoordinates(null);
    if (!textAreaRef.current || selectedMentionStartPosition === null) {
      return;
    }
    const currentValue = textAreaRef.current.value;
    const beforeMention = currentValue.slice(0, selectedMentionStartPosition); // Text before the @
    const afterMention = currentValue.slice(textAreaRef.current.selectionStart); // Text after the @
    // Add single space after mention, trimming any existing spaces
    const newValue_0 = `${beforeMention}@${user.name} ${afterMention.trimStart()}`;

    // Adjust cursor position to after the mention and space
    const newCursorPosition = selectedMentionStartPosition + user.name.length + 2; // +2 for @ and space
    commitTextareaValue(newValue_0, {
      selectionStart: newCursorPosition
    });
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
        textAreaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };
  const handleFileInputChange = async (e_2: React.ChangeEvent<HTMLInputElement>) => {
    const file = e_2.target.files?.[0];
    if (file && textAreaRef.current) {
      const validatedFile = validateFile(file);
      if (!validatedFile || !repository?.id) {
        return;
      }

      // Start the upload but first sync React state with the placeholder that was just inserted
      const uploadPromise = handleFileUpload(file, textAreaRef.current, repository.id, textAreaRef.current.selectionEnd, onChange);

      // Immediately update state so the placeholder appears even if React re-renders
      const placeholderValue = textAreaRef.current.value;
      commitTextareaValue(placeholderValue, {
        emitChange: false
      });

      // Wait for upload to finish and then update with the final markdown tag
      const newValue_1 = await uploadPromise;
      commitTextareaValue(newValue_1, {
        emitChange: false
      });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleGifSelect = (gif: {
    url: string;
    title: string;
  }) => {
    trackClientEvent('gif_selected', undefined);
    if (!textAreaRef?.current) {
      return;
    }
    const newValue_2 = insertGif(gif, textAreaRef.current, textAreaRef.current.selectionEnd, onChange);
    commitTextareaValue(newValue_2, {
      emitChange: false
    });
    textAreaRef.current.focus();
  };
  const handlePaste = async (e_3: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e_3.clipboardData.files);
    const file_0 = files[0]; // Take first file from clipboard

    if (file_0 && textAreaRef.current && repository?.id) {
      const validatedFile_0 = validateFile(file_0);
      e_3.preventDefault();
      if (!validatedFile_0) {
        return;
      }
      const newValue_3 = await handleFileUpload(file_0, textAreaRef.current, repository.id, e_3.currentTarget.selectionStart, onChange);
      commitTextareaValue(newValue_3, {
        emitChange: false
      });
      return;
    }
    const pastedLink = createMarkdownLinkFromPastedUrl({
      value: e_3.currentTarget.value,
      selectionStart: e_3.currentTarget.selectionStart,
      selectionEnd: e_3.currentTarget.selectionEnd,
      pastedText: e_3.clipboardData.getData('text/plain') || e_3.clipboardData.getData('text')
    });
    if (pastedLink) {
      e_3.preventDefault();
      commitTextareaValue(pastedLink.value, {
        selectionStart: pastedLink.selectionStart
      });
      requestAnimationFrame(() => {
        const textarea_0 = textAreaRef.current;
        if (textarea_0 && document.body.contains(textarea_0)) {
          textarea_0.focus();
          textarea_0.setSelectionRange(pastedLink.selectionStart, pastedLink.selectionStart);
        }
      });
    }
  };

  /**
   * Allow dropping a file anywhere on the textarea wrapper (not just inside the HTMLTextAreaElement).
   * We delegate to the existing handleDrop logic by forwarding the event to the textarea ref.
   */
  const handleWrapperDragOver = (e_4: React.DragEvent<HTMLDivElement>) => {
    // Needed so that the drop event actually fires
    if (e_4.dataTransfer.types.includes('Files')) {
      e_4.preventDefault();
    }
  };
  const handleWrapperDrop = async (e_5: React.DragEvent<HTMLDivElement>) => {
    // Prevent default browser behaviour (opening the file)
    e_5.preventDefault();
    const files_0 = Array.from(e_5.dataTransfer.files);
    const file_1 = files_0[0];
    if (file_1 && textAreaRef.current && repository?.id) {
      const validatedFile_1 = validateFile(file_1);
      if (!validatedFile_1) {
        return;
      }

      // Start upload and immediately reflect placeholder in state
      const uploadPromise_0 = handleFileUpload(file_1, textAreaRef.current, repository.id, textAreaRef.current.selectionEnd, onChange);
      const placeholderValue_0 = textAreaRef.current.value;
      commitTextareaValue(placeholderValue_0, {
        emitChange: false
      });
      const newValue_4 = await uploadPromise_0;
      commitTextareaValue(newValue_4, {
        emitChange: false
      });
    }
  };
  const handleAiSuggestionClick = async () => {
    if (!onAiSuggestion || replyCodeContext === undefined || isGeneratingAiSuggestionRef.current) {
      return;
    }
    const comment = triggerAutoSize.trim();
    if (!comment) {
      toast.error('Comment is required to use AI suggestion');
      return;
    }
    try {
      isGeneratingAiSuggestionRef.current = true;
      setIsLoadingAiSuggestion(true);
      await onAiSuggestion({
        comment,
        replyCodeContext,
        fileContext
      }, (streamText: string) => {
        const sanitizedStreamText = streamText.replace(/\\n/g, '\n');
        const newValue_5 = comment + (comment ? '\n\n' : '') + sanitizedStreamText;
        // Batch stream chunks so one suggestion doesn't flood the undo stack.
        commitTextareaValue(newValue_5, {
          batch: true,
          emitChange: false,
          selectionStart: newValue_5.length
        });
      });
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to get AI suggestion');
    } finally {
      isGeneratingAiSuggestionRef.current = false;
      setIsLoadingAiSuggestion(false);
    }
  };
  const handleSubmitInternal = () => {
    if (variant !== TEXTAREA_VARIANTS.editingSavedContent && !hasText ||
    // Don't allow submission if no text (unless editingSavedContent)
    !handleSubmit) {
      return;
    }
    handleSubmit(triggerAutoSize);
    commitTextareaValue('', {
      emitChange: false,
      selectionStart: 0
    });
  };
  const handleAddToReviewInternal = () => {
    if (!onAddToReview) {
      console.error('onAddToReview is not defined');
      return;
    }
    onAddToReview(triggerAutoSize);
    commitTextareaValue('', {
      emitChange: false,
      selectionStart: 0
    });
  };
  const handleCancelInternal = () => {
    if (!handleCancel) {
      return;
    }
    handleCancel();
  };
  const handleKeyDown = (e_6: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e_6.key === 'Escape' && !isHandlingPopoverEscape.current) {
      // Only blur on escape if we're not already handling it in the Popover
      // This prevents double-handling when closing the mentions dropdown
      e_6.preventDefault();
      textAreaRef.current?.blur();
    }

    // If mentions dropdown is open, let it handle the Enter key
    if (e_6.key === 'Enter' && showMentionsDropdown) {
      e_6.preventDefault();
      return;
    }

    // Handle form submission
    if (e_6.key === 'Enter' && handleSubmit) {
      if (e_6.metaKey && e_6.shiftKey && !disablePostImmediately) {
        // Command+Shift+Enter to post immediately
        e_6.preventDefault();
        handleSubmitInternal();
      } else if (e_6.metaKey) {
        // Command+Enter to add to review (for non-PR replies) or submit (for PR replies)
        e_6.preventDefault();
        if (variant === TEXTAREA_VARIANTS.prComment) {
          handleAddToReviewInternal();
        } else {
          handleSubmitInternal();
        }
      }
    }
  };
  useAutosizeTextArea({
    textAreaRef,
    triggerAutoSize,
    maxHeight,
    minHeight
  });
  useImperativeHandle(ref, () => ({
    textArea: textAreaRef.current as HTMLTextAreaElement,
    focus: () => textAreaRef?.current?.focus(),
    maxHeight,
    minHeight,
    clear: () => {
      commitTextareaValue('', {
        emitChange: false,
        selectionStart: 0
      });
    }
  }));
  return <div className="relative w-full" style={{
    contain: 'content'
  }} onDragOver={handleWrapperDragOver} onDrop={handleWrapperDrop}>
      <input type="file" ref={fileInputRef} className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none" onChange={handleFileInputChange} accept="*/*" aria-label="Upload file" title="Upload any file (max 10MB)" data-testid="file-upload-input" disabled={isLoadingRepository} // Disable while loading repository data
    />

      {/* Textarea Container */}
      <div className="flex flex-col">
        <div className={cn('relative rounded-md')}>
          <textarea ref={textAreaRef} placeholder={props.placeholder} onChange={handleTextChange} onKeyDown={handleKeyDown} onPaste={handlePaste} value={triggerAutoSize} suppressHydrationWarning className={cn('w-full resize-none overflow-hidden', 'bg-zinc-900 px-1 py-4', 'text-sm ring-offset-background', 'placeholder:text-base placeholder:text-zinc-500', 'md:text-base', 'focus-visible:outline-hidden', 'disabled:cursor-not-allowed disabled:opacity-50', 'autosize-textarea-element', variant === TEXTAREA_VARIANTS.default && 'pr-24', className)} style={{
          minHeight,
          maxHeight
        }} {...props} />

          {/* Buttons rendered inside textarea container only for non-reply variants */}
          {variant !== TEXTAREA_VARIANTS.reply && variant !== TEXTAREA_VARIANTS.prReply && <TextareaButtons handleGifSelectAction={handleGifSelect} hasText={hasText} onFileClickAction={() => fileInputRef.current?.click()} showSendButton={showSendButton} variant={variant} onAiSuggestion={onAiSuggestion ? handleAiSuggestionClick : undefined} isLoadingAiSuggestion={isLoadingAiSuggestion} onGetCodeLine={onGetCodeLine} onSubmit={handleSubmitInternal} onCancel={handleCancelInternal} onAddToReview={handleAddToReviewInternal} extraButtonNodes={extraButtonNodes} textAreaRef={textAreaRef as RefObject<HTMLTextAreaElement>} side={side} />}
        </div>

        {/* Buttons rendered below textarea only for reply and prReply variants */}
        {(variant === TEXTAREA_VARIANTS.reply || variant === TEXTAREA_VARIANTS.prReply) && <TextareaButtons handleGifSelectAction={handleGifSelect} hasText={hasText} onFileClickAction={() => fileInputRef.current?.click()} showSendButton={showSendButton} variant={variant} onAiSuggestion={onAiSuggestion ? handleAiSuggestionClick : undefined} isLoadingAiSuggestion={isLoadingAiSuggestion} onGetCodeLine={onGetCodeLine} onSubmit={handleSubmitInternal} onCancel={handleCancelInternal} disablePostImmediately={disablePostImmediately} extraButtonNodes={extraButtonNodes} textAreaRef={textAreaRef as RefObject<HTMLTextAreaElement>} side={side} />}
      </div>

      {/* Mentions Dropdown */}
      <PopoverPrimitive.Root open={showMentionsDropdown} modal={false}>
        <PopoverPrimitive.Anchor asChild>
          <div className="absolute" style={{
          position: 'absolute',
          left: `${(mentionCoordinates?.left ?? 0) - 20}px`,
          top: `${(mentionCoordinates?.top ?? 0) + (mentionCoordinates?.height ?? 0)}px`,
          opacity: mentionCoordinates ? 1 : 0 // Only show when we have coordinates
        }} />
        </PopoverPrimitive.Anchor>
        {showMentionsDropdown && <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content className={cn('w-[200px] rounded-md border bg-popover p-0 shadow-md outline-hidden', 'animate-in fade-in-0 zoom-in-95', 'data-[side=top]:-translate-y-7', 'z-60')} side="bottom" align="start" sideOffset={0} alignOffset={0} collisionPadding={16} avoidCollisions onOpenAutoFocus={e_7 => e_7.preventDefault()} onCloseAutoFocus={e_8 => e_8.preventDefault()} onEscapeKeyDown={e_9 => {
          // When escape is pressed with mentions open:
          // 1. Prevent default escape behavior
          e_9.preventDefault();
          // 2. Set flag to prevent textarea from also handling escape
          isHandlingPopoverEscape.current = true;
          // 3. Close the mentions dropdown
          setShowMentionsDropdown(false);
          setMentionStartPosition(null);
          setMentionCoordinates(null);
          // 4. Reset the flag after handlers finish
          // This needs to be async to ensure our keydown handler runs first
          setTimeout(() => {
            isHandlingPopoverEscape.current = false;
          }, 0);
        }} onPointerDownOutside={() => {
          setShowMentionsDropdown(false);
          setMentionCoordinates(null);
        }}>
              {enableUserMentions && mentionableUsers && <MentionableUsersDropdown users={mentionableUsers} owner={owner} searchQuery={mentionSearchQuery} onSelect={handleMentionSelect} />}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>}
      </PopoverPrimitive.Root>
    </div>;
};
AutosizeTextarea.displayName = 'AutosizeTextarea';